// FE-SCRM-049 — Prospects tab panel.
//
// Replaces the empty-state placeholder in SalesCrmPage.tsx with a
// real list of the entity's book of business. The BE returns all
// rows in one call (BE-SCRM-003 — acceptance ≤ 1000 prospects per
// entity); search / multi-select filters / sort / pagination run
// client-side over that snapshot so the URL stays the canonical
// source of truth for deep links.
//
// What's intentionally NOT on the panel yet:
// * Owner column + filter. ``SalesProspect`` has no ``owner_id``
//   column today (owner / producer concept lives on
//   ``SalesOpportunity``). When the prospect owner field lands,
//   add a column + add ``owner`` to the filter codec.
// * Tags column + filter. The /api/v1/sales/tags/suggestions
//   endpoint is read-only and there's no prospect tag-link write
//   surface yet (see ``backend/sales/tag_views.py``); the column
//   will surface once the link surface ships.
// * last_touchpoint_at column. The Renewal Board's opportunity
//   serializer computes this; the prospect serializer does not.
//   Trivial BE add when wanted.

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import {
  Badge, Table, TBody, THead, Tr, Th as DsTh, Td as DsTd,
} from "@/design-system";

import {
  CrmSearchInput,
  IncludeArchivedToggle,
  Pagination,
  RowActionsMenu,
  readIncludeArchivedFromUrl,
  readPaginationFromUrl,
  readSearchFromUrl,
} from "../components";

import { AddProspectModal } from "./AddProspectModal";
import { ProspectsFiltersBar } from "./ProspectsFiltersBar";
import {
  filterProspectRows,
  readProspectFiltersFromUrl,
  readSortFromUrl,
  sortProspectRows,
  writeSortToParams,
} from "./filterState";
import {
  useArchiveProspectMutation,
  useProspectsQuery,
  useRestoreProspectMutation,
} from "./hooks";
import {
  LIVES_BAND_LABEL,
  MONTH_LABEL,
  SOURCE_LABEL,
  type Prospect,
  type SortKey,
} from "./types";


const VISIBLE_SORTS: { key: SortKey; label: string }[] = [
  { key: "name",         label: "Name (A→Z)" },
  { key: "-name",        label: "Name (Z→A)" },
  { key: "renewal",      label: "Renewal soonest" },
  { key: "-renewal",     label: "Renewal latest" },
  { key: "-fit_score",   label: "Fit score (high)" },
  { key: "fit_score",    label: "Fit score (low)" },
  { key: "-created_at",  label: "Newest" },
  { key: "created_at",   label: "Oldest" },
];


export function ProspectsPanel(): JSX.Element {
  const [search, setSearch] = useSearchParams();
  const [modal_open, setModalOpen] = useState(false);

  const include_archived = readIncludeArchivedFromUrl(search);
  const filters = useMemo(
    () => ({
      ...readProspectFiltersFromUrl(search),
      q: readSearchFromUrl(search),
    }),
    [search],
  );
  const sort = readSortFromUrl(search);
  const pagination = readPaginationFromUrl(search);

  // FE-SCRM-063 — the form5500_status filter is BE-applied (the FE
  // doesn't carry ``form5500_match_attempted_at`` in the row shape,
  // so it can't bucket needs_research vs not_yet_attempted client-
  // side). Plumbing the param into the list query forces a refetch
  // whenever the chip flips.
  const list_query = useProspectsQuery({
    include_archived,
    form5500_status: filters.form5500_status,
  });
  const archive_mutation = useArchiveProspectMutation();
  const restore_mutation = useRestoreProspectMutation();

  const filtered = useMemo(() => {
    const all = list_query.data?.rows ?? [];
    // The BE already scopes by ``?include_archived=`` but we belt-
    // and-braces client-side: a stale TanStack cache after the
    // toggle flips on/off would otherwise leak the wrong rows for
    // one frame.
    const scoped = include_archived
      ? all : all.filter((r) => r.status !== "archived");
    return sortProspectRows(filterProspectRows(scoped, filters), sort);
  }, [list_query.data, filters, sort, include_archived]);

  const total = filtered.length;
  const start = (pagination.page - 1) * pagination.pageSize;
  const page_rows = filtered.slice(start, start + pagination.pageSize);

  function onSortChange(next: SortKey) {
    setSearch(writeSortToParams(next, search), { replace: true });
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <div data-testid="prospects-panel" className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col">
          <h2 className="text-base font-semibold text-text-primary">
            Prospects
          </h2>
          <p className="text-xs text-text-secondary">
            Your book of business. Each row is anchored to a renewal
            month — the single most important attribute in benefits.
          </p>
        </div>
        <button
          type="button"
          data-testid="prospects-add-button"
          onClick={() => setModalOpen(true)}
          className={[
            "inline-flex items-center gap-1 rounded-md bg-teal",
            "px-3 py-1.5 text-sm font-medium text-text-inverse",
            "hover:opacity-90",
          ].join(" ")}
        >
          + Add prospect
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <CrmSearchInput
          placeholder="Search by name, EIN, or current broker"
          className="w-72"
        />
        <IncludeArchivedToggle />
        <div className="ml-auto flex items-center gap-2 text-xs text-text-secondary">
          <span>Sort</span>
          <select
            aria-label="Sort prospects"
            value={sort}
            onChange={(e) => onSortChange(e.target.value as SortKey)}
            className={[
              "rounded-md border border-gray-300 bg-white px-2 py-1",
              "text-xs focus:border-indigo-500 focus:outline-none",
              "focus:ring-1 focus:ring-indigo-500",
            ].join(" ")}
          >
            {VISIBLE_SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <ProspectsFiltersBar total_rows={total} />

      {list_query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : list_query.isError ? (
        <DashboardErrorState
          title="Couldn't load prospects"
          description="Refresh to retry, or check the network connection."
          retry={() => list_query.refetch()}
        />
      ) : total === 0 ? (
        <ProspectsEmptyState
          has_any_rows={(list_query.data?.rows.length ?? 0) > 0}
        />
      ) : (
        <>
          <ProspectsTable
            rows={page_rows}
            onArchive={(id) => archive_mutation.mutate(id)}
            onRestore={(id) => restore_mutation.mutate(id)}
          />
          <Pagination total={total} />
        </>
      )}

      <AddProspectModal
        open={modal_open}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}


function ProspectsEmptyState({
  has_any_rows,
}: { has_any_rows: boolean }): JSX.Element {
  // Two empty states:
  // 1) The entity has zero prospects → onboarding moment with the
  //    Add + Import CTAs prominent.
  // 2) The entity has rows but the current filters wiped them all →
  //    softer "no matches" message with a Clear filters CTA.
  if (has_any_rows) {
    return (
      <DashboardEmptyState
        title="No prospects match these filters."
        description={
          "Clear the search or filter chips to see your full book, "
          + "or switch to research mode to surface employers from "
          + "Form 5500 data."
        }
        action={
          <Link
            data-testid="prospects-empty-clear-filters-cta"
            to="/dashboard/sales-crm?tab=prospects"
            className={[
              "inline-flex items-center justify-center rounded-md",
              "bg-teal px-4 py-2 text-sm font-medium",
              "text-text-inverse hover:opacity-90",
            ].join(" ")}
          >
            Clear filters
          </Link>
        }
      />
    );
  }
  return (
    <div data-testid="prospects-empty">
      <DashboardEmptyState
        title="No prospects yet"
        description={
          "Add the employers you're pursuing — one at a time as you "
          + "meet them, or bulk-import your book of business."
        }
        action={
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="prospects-empty-add-cta"
              onClick={() => {
                document.dispatchEvent(
                  new CustomEvent("prospects-open-add-modal"),
                );
              }}
              className={[
                "inline-flex items-center justify-center rounded-md",
                "bg-teal px-4 py-2 text-sm font-medium",
                "text-text-inverse hover:opacity-90",
              ].join(" ")}
            >
              + Add prospect
            </button>
            <Link
              data-testid="prospects-empty-import-cta"
              to="/dashboard/sales-crm/import?type=prospects"
              className={[
                "inline-flex items-center justify-center rounded-md",
                "border border-gray-300 bg-white px-4 py-2 text-sm",
                "font-medium text-text-primary hover:bg-gray-50",
              ].join(" ")}
            >
              Import CSV
            </Link>
          </div>
        }
      />
    </div>
  );
}


function ProspectsTable({
  rows, onArchive, onRestore,
}: {
  rows: Prospect[];
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
}): JSX.Element {
  return (
    <Table
      density="xs"
      dividers
      data-testid="prospects-table"
      className="min-w-full divide-y divide-border-subtle"
      wrapperClassName="rounded-md border border-border-subtle"
    >
      <THead className="bg-recessed text-text-secondary">
        <Tr>
          <Th>Name</Th>
          <Th>Renewal</Th>
          <Th>Lives</Th>
          <Th>Current broker</Th>
          <Th>Source</Th>
          <Th>Fit</Th>
          <Th>Created</Th>
          <Th className="text-right pr-2">Actions</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => (
          <ProspectRow
            key={row.prospect_id}
            row={row}
            onArchive={onArchive}
            onRestore={onRestore}
          />
        ))}
      </TBody>
    </Table>
  );
}


function ProspectRow({
  row, onArchive, onRestore,
}: {
  row: Prospect;
  onArchive: (id: string) => void;
  onRestore: (id: string) => void;
}): JSX.Element {
  const archived = row.status === "archived";
  const actions = archived
    ? [{ label: "Unarchive", onClick: () => onRestore(row.prospect_id) }]
    : [{
        label: "Archive",
        onClick: () => onArchive(row.prospect_id),
        destructive: false as const,
      }];

  return (
    <Tr
      data-testid={`prospect-row-${row.prospect_id}`}
      className={archived ? "text-text-tertiary opacity-70" : ""}
    >
      <Td>
        <Link
          to={`/dashboard/sales-crm/prospects/${row.prospect_id}`}
          className="font-medium text-text-primary hover:underline"
        >
          {row.name}
        </Link>
        {archived && (
          <span
            className="ml-2 rounded-full bg-recessed px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-text-tertiary"
          >
            Archived
          </span>
        )}
      </Td>
      <Td>{MONTH_LABEL[row.renewal_month]} {row.renewal_year}</Td>
      <Td>{row.lives_band ? LIVES_BAND_LABEL[row.lives_band] : "—"}</Td>
      <Td>{row.current_broker_firm ?? "—"}</Td>
      <Td>{SOURCE_LABEL[row.source]}</Td>
      <Td>{row.fit_score !== null ? (
        <Badge variant={fitVariant(row.fit_score)}>{row.fit_score}</Badge>
      ) : "—"}</Td>
      <Td>{row.created_at ? row.created_at.slice(0, 10) : "—"}</Td>
      <Td className="pr-2 text-right">
        <RowActionsMenu items={actions} />
      </Td>
    </Tr>
  );
}


// Thin wrappers over the design-system header/data cells that add this
// panel's header styling (11px uppercase) and no-wrap cells. Padding +
// font-weight come from the shared cell; the table routes through Table.
function Th({ children, className }: {
  children: React.ReactNode; className?: string;
}) {
  return (
    <DsTh
      className={[
        "whitespace-nowrap text-[11px] uppercase tracking-wide",
        className ?? "",
      ].join(" ")}
    >
      {children}
    </DsTh>
  );
}


function Td({ children, className }: {
  children: React.ReactNode; className?: string;
}) {
  return (
    <DsTd className={["whitespace-nowrap", className ?? ""].join(" ")}>
      {children}
    </DsTd>
  );
}


// Badge supports category / regulatory / neutral; map fit-score
// buckets onto the design-system palette (high→category green,
// mid→regulatory amber, low→neutral).
function fitVariant(score: number): "category" | "regulatory" | "neutral" {
  if (score >= 70) return "category";
  if (score >= 40) return "regulatory";
  return "neutral";
}
